import { useState, useEffect } from 'react';
import { storageService } from './services/storage';
import { UserProgress } from './services/storage';
import HomePage from './pages/HomePage';
import VocabularyPage from './pages/VocabularyPage';
import PhrasesPage from './pages/PhrasesPage';
import ListeningPage from './pages/ListeningPage';
import SpeakingPage from './pages/SpeakingPage';
import ProfilePage from './pages/ProfilePage';
import WelcomePage from './pages/WelcomePage';
import PhoneticsPage from './pages/PhoneticsPage';
import NaturalPhonicsPage from './pages/NaturalPhonicsPage';
import AIConversationPage from './pages/AIConversationPage';
import GamesPage from './pages/GamesPage';
import DailyTaskPage from './pages/DailyTaskPage';
import MistakeReviewPage from './pages/MistakeReviewPage';
import VirtualPetPage from './pages/VirtualPetPage';
import PasswordProtection from './components/PasswordProtection';
import { ToastProvider } from './components/common/ToastContext';

import KETTrainingPage from './pages/KETTrainingPage';

type Page = 'home' | 'vocabulary' | 'phrases' | 'listening' | 'speaking' | 'phonetics' | 'natural-phonics' | 'profile' | 'ai-chat' | 'games' | 'daily-task' | 'mistake-review' | 'virtual-pet' | 'ket-training';

export default function App() {
  const [user, setUser] = useState<UserProgress | null>(null);
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [loading, setLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    // Load current user from storage
    const currentUser = storageService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const handleCreateUser = (name: string, age: number, avatar: string) => {
    const newUser = storageService.createUser(name, age, avatar);
    setUser(newUser);
  };

  const handleSelectUser = (userId: string) => {
    storageService.setCurrentUser(userId);
    const selectedUser = storageService.getUser(userId);
    setUser(selectedUser);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">📚</div>
          <p className="text-gray-500 text-lg">加载中...</p>
        </div>
      </div>
    );
  }

  // Main app content
  const appContent = (
    <>
      {/* Show welcome page if no user */}
      {!user ? (
        <WelcomePage
          onCreateUser={handleCreateUser}
          onSelectUser={handleSelectUser}
          existingUsers={storageService.getAllUsers()}
        />
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-50 to-purple-50">
          {/* Top bar */}
          <div className="glass sticky top-0 z-40 border-b border-white/20">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
              {/* Left side: Back button + User info */}
              <div className="flex items-center gap-2">
                {/* Back button - only show when not on home page or KET training (KET has its own navigation) */}
                {currentPage !== 'home' && currentPage !== 'ket-training' && (
                  <button
                    onClick={() => setCurrentPage('home')}
                    className="p-2 rounded-xl bg-white/50 text-gray-600 hover:bg-white/80 transition-all flex items-center gap-1"
                  >
                    <span className="text-lg">←</span>
                    <span className="hidden sm:inline text-sm">返回</span>
                  </button>
                )}

                {/* User info */}
                <div className="flex items-center gap-2 ml-2">
                  <span className="text-2xl sm:text-3xl">{user.avatar}</span>
                  <div className="hidden md:block">
                    <p className="font-bold text-gray-800">{user.name}</p>
                    <p className="text-sm text-gray-500">
                      Lv.{user.level} · {user.points}分
                    </p>
                  </div>
                </div>
              </div>

              {/* Navigation - Desktop */}
              <div className="hidden md:flex gap-2">
                <NavButton
                  icon="🏠"
                  label="首页"
                  active={currentPage === 'home'}
                  onClick={() => setCurrentPage('home')}
                />
                <NavButton
                  icon="📚"
                  label="学习"
                  active={currentPage === 'vocabulary'}
                  onClick={() => setCurrentPage('vocabulary')}
                />
                <NavButton
                  icon="🎮"
                  label="游戏"
                  active={currentPage === 'games'}
                  onClick={() => setCurrentPage('games')}
                />
                <NavButton
                  icon="🤖"
                  label="AI对话"
                  active={currentPage === 'ai-chat'}
                  onClick={() => setCurrentPage('ai-chat')}
                />
                <NavButton
                  icon="👤"
                  label="我的"
                  active={currentPage === 'profile'}
                  onClick={() => setCurrentPage('profile')}
                />
              </div>

              {/* Navigation - Mobile (simplified) */}
              <div className="flex md:hidden items-center gap-2">
                {/* Home button always visible */}
                <NavButton
                  icon="🏠"
                  label=""
                  active={currentPage === 'home'}
                  onClick={() => setCurrentPage('home')}
                />

                {/* More menu button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="p-2 rounded-xl bg-white/50 text-gray-600 hover:bg-white/80 transition-all"
                >
                  <span className="text-lg">☰</span>
                </button>

                {/* Mobile dropdown menu */}
                {showMobileMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setShowMobileMenu(false)}
                    />
                    <div className="absolute right-4 top-full mt-2 w-48 bg-white rounded-2xl shadow-xl z-40 py-2 border border-gray-100">
                      <MobileMenuItem
                        icon="📚"
                        label="学习"
                        active={currentPage === 'vocabulary'}
                        onClick={() => { setCurrentPage('vocabulary'); setShowMobileMenu(false); }}
                      />
                      <MobileMenuItem
                        icon="🎮"
                        label="游戏"
                        active={currentPage === 'games'}
                        onClick={() => { setCurrentPage('games'); setShowMobileMenu(false); }}
                      />
                      <MobileMenuItem
                        icon="🤖"
                        label="AI对话"
                        active={currentPage === 'ai-chat'}
                        onClick={() => { setCurrentPage('ai-chat'); setShowMobileMenu(false); }}
                      />
                      <MobileMenuItem
                        icon="👤"
                        label="我的"
                        active={currentPage === 'profile'}
                        onClick={() => { setCurrentPage('profile'); setShowMobileMenu(false); }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Main content */}
          <main className="max-w-7xl mx-auto px-4 py-8">
            {currentPage === 'home' && <HomePage user={user} onNavigate={(page) => setCurrentPage(page as Page)} />}
            {currentPage === 'vocabulary' && <VocabularyPage user={user} />}
            {currentPage === 'phrases' && <PhrasesPage user={user} />}
            {currentPage === 'listening' && <ListeningPage user={user} />}
            {currentPage === 'speaking' && <SpeakingPage user={user} />}
            {currentPage === 'phonetics' && <PhoneticsPage />}
            {currentPage === 'natural-phonics' && <NaturalPhonicsPage />}
            {currentPage === 'profile' && <ProfilePage user={user} onNavigate={(page) => setCurrentPage(page as Page)} />}
            {currentPage === 'ai-chat' && <AIConversationPage user={user} />}
            {currentPage === 'games' && <GamesPage user={user} />}
            {currentPage === 'daily-task' && <DailyTaskPage user={user} />}
            {currentPage === 'mistake-review' && <MistakeReviewPage user={user} />}
            {currentPage === 'ket-training' && <KETTrainingPage user={user} onNavigate={(page) => setCurrentPage(page as Page)} />}
          </main>

          {/* Footer */}
          <footer className="text-center py-6 text-gray-400 text-sm">
            <p>完全免费的英语学习应用 💜 无需付费</p>
            <p className="mt-2">使用浏览器内置语音功能，零成本学习</p>
          </footer>
        </div>
      )}
    </>
  );

  // Wrap with password protection and toast provider
  return (
    <PasswordProtection>
      <ToastProvider>
        {appContent}
      </ToastProvider>
    </PasswordProtection>
  );
}

// Navigation button component
function NavButton({ icon, label, active, onClick }: {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-xl font-medium transition-all ${
        active
          ? 'bg-primary text-white shadow-lg scale-105'
          : 'bg-white/50 text-gray-600 hover:bg-white/80'
      }`}
    >
      <span>{icon}</span>
      {label && <span className="hidden sm:inline ml-1">{label}</span>}
    </button>
  );
}

// Mobile menu item component
function MobileMenuItem({ icon, label, active, onClick }: {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
        active
          ? 'bg-primary/10 text-primary'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <span className="text-xl">{icon}</span>
      <span className="font-medium">{label}</span>
    </button>
  );
}